import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import _ from 'lodash';
import { useSnapshot } from 'valtio';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  Tooltip,
  TabPane,
} from 'reactstrap';
import Select from 'react-select';
import { optionizeAll } from 'helpers/select';
import { useArrayStateObject } from 'components/CustomHooks';
import { optimizationState } from '../state';

function Assumptions({ optimizerOptions }) {
  const snap = useSnapshot(optimizationState);
  const returnAssumptionOption = useArrayStateObject({ initialItems: optionizeAll(['Historical', 'User Set']) });
  const timePeriodOption = useArrayStateObject({ initialItems: optionizeAll([12, 24, 36, 'Commonized']) });
  const optimizationGoalOption = useArrayStateObject({ initialItems: [] });

  useEffect(() => {
    if (!optimizerOptions) return;
    const { objectives_options } = optimizerOptions;

    const goalOptions = Object.entries(objectives_options).map(([label, value]) => ({ label, value }));
    optimizationGoalOption.setItems(goalOptions);
    if (goalOptions.length > 0) optimizationGoalOption.setSelected(goalOptions[0]);
  }, [optimizerOptions]);

  useEffect(() => {
    optimizationState.returnAssumption = returnAssumptionOption.selected.value;
    optimizationState.timePeriod = timePeriodOption.selected.value;
    optimizationState.optimizationGoal = optimizationGoalOption.selected?.value ?? null;
    if (!optimizationGoalOption.selected?.label.includes('subject to')) {
      optimizationState.targetReturn = 0;
    }
  }, [returnAssumptionOption.selected, timePeriodOption.selected, optimizationGoalOption.selected]);

  return (
    <div className="tw-max-w-3xl tw-mx-auto">
      <Card>
        <CardHeader>
          <CardTitle tag="h3" className="text-center">
            Optimization Assumptions
          </CardTitle>
        </CardHeader>
        <CardBody className="mt-3">
          <Row>
            <Col sm="3" className="text-right">
              <Label>Returns Assumption</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <Select
                  className="react-select"
                  name="returnAssumptionOption"
                  value={returnAssumptionOption.selected}
                  onChange={returnAssumptionOption.setSelected}
                  options={returnAssumptionOption.items}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="3" className="text-right">
              <Label>Time Period for Risk</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <Select
                  className="react-select"
                  name="timePeriodOption"
                  value={timePeriodOption.selected}
                  onChange={timePeriodOption.setSelected}
                  options={timePeriodOption.items}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="3" className="text-right">
              <Label>Optimization Goal</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <Select
                  className="react-select"
                  name="optimizationGoalOption"
                  value={optimizationGoalOption.selected}
                  onChange={optimizationGoalOption.setSelected}
                  options={optimizationGoalOption.items}
                />
              </FormGroup>
            </Col>
          </Row>
          {optimizationGoalOption.selected && optimizationGoalOption.selected.label.includes('subject to') && (
            <Row>
              <Col sm="3" className="text-right">
                <Label>Target Return/Risk</Label>
              </Col>
              <Col sm="7">
                <FormGroup>
                  <Input
                    type="number"
                    min={-100}
                    max={100}
                    value={snap.targetReturn}
                    onChange={(event) => {
                      optimizationState.targetReturn = parseFloat(event.target.value);
                    }}
                    onWheel={(event) => {
                      event.target?.blur();
                      event.currentTarget?.blur();
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
          )}
          <Row>
            <Col sm="3"></Col>
            <Col sm="7">
              <FormGroup check>
                <Label check className="tw-select-none">
                  <Input
                    checked={snap.assetConstraint}
                    type="checkbox"
                    onChange={(e) => {
                      optimizationState.assetConstraint = e.target.checked;
                    }}
                  />
                  <span className="form-check-sign" />
                  Asset Level Constraints
                </Label>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="3"></Col>
            <Col sm="7">
              <FormGroup check>
                <Label check className="tw-select-none">
                  <Input
                    checked={snap.groupConstraint}
                    type="checkbox"
                    onChange={(e) => {
                      optimizationState.groupConstraint = e.target.checked;
                    }}
                  />
                  <span className="form-check-sign" />
                  Group Constraints
                </Label>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
}

export default Assumptions;
